/** JA. Creations — geometry-stable card variant media. */
import { StandardEvents } from '@shopify/events';

if (!window.__jaCardVariantMediaBound) {
  window.__jaCardVariantMediaBound = true;

  const decoded = new Map();
  const state = new WeakMap();
  const observed = new WeakSet();

  const galleryOf = (card) => {
    if (!(card instanceof HTMLElement)) return null;
    const gallery = card.querySelector('[ref="cardGallery"][data-ja-variant-media="true"]');
    return gallery instanceof HTMLElement ? gallery : null;
  };

  const slidesOf = (gallery) =>
    gallery ? [...gallery.querySelectorAll('[data-ja-group] [data-ja-slide]')] : [];

  const imageOf = (slide) => {
    const image = slide?.querySelector('.product-media img, img');
    return image instanceof HTMLImageElement ? image : null;
  };

  const descriptorFromInput = (input, liveImage) => {
    const src = input.dataset.jaPreviewSrc || '';
    if (!src) return null;

    return {
      src,
      srcset: input.dataset.jaPreviewSrcset || '',
      sizes: liveImage?.getAttribute('sizes') || input.dataset.jaPreviewSizes || '',
      alt: input.dataset.jaPreviewAlt || '',
    };
  };

  const descriptorFromImage = (sourceImage, liveImage) => {
    const src = sourceImage.getAttribute('src') || sourceImage.src || '';
    if (!src) return null;

    return {
      src,
      srcset: sourceImage.getAttribute('srcset') || '',
      sizes: liveImage?.getAttribute('sizes') || sourceImage.getAttribute('sizes') || '',
      alt: sourceImage.alt || '',
    };
  };

  const descriptorKey = ({ src, srcset, sizes }) => `${src}\n${srcset}\n${sizes}`;

  const decodeResource = (descriptor, priority = 'low') => {
    if (!descriptor?.src) return Promise.resolve(false);

    const key = descriptorKey(descriptor);
    const cached = decoded.get(key);
    if (cached) return cached;

    const image = new Image();
    image.decoding = 'async';
    image.fetchPriority = priority;
    if (descriptor.sizes) image.sizes = descriptor.sizes;
    if (descriptor.srcset) image.srcset = descriptor.srcset;
    image.src = descriptor.src;

    const promise = (async () => {
      if (!image.complete) {
        const loaded = await new Promise((resolve) => {
          image.addEventListener('load', () => resolve(true), { once: true });
          image.addEventListener('error', () => resolve(false), { once: true });
        });
        if (!loaded) return false;
      }

      if (!image.naturalWidth) return false;

      if (typeof image.decode === 'function') {
        try {
          await image.decode();
        } catch {
          if (!image.complete || !image.naturalWidth) return false;
        }
      }

      return image.complete && image.naturalWidth > 0;
    })().then((ready) => {
      if (!ready) decoded.delete(key);
      return ready;
    });

    decoded.set(key, promise);
    return promise;
  };

  const applyImage = (image, descriptor) => {
    if (!image || !descriptor?.src) return;

    // Geometry is intentionally untouched: no width/height/style/class changes.
    if (descriptor.sizes) image.sizes = descriptor.sizes;
    if (descriptor.srcset) image.srcset = descriptor.srcset;
    else image.removeAttribute('srcset');
    image.src = descriptor.src;
    image.alt = descriptor.alt;
  };

  const activeIndexOf = (gallery) => {
    const segments = [...gallery.querySelectorAll('[data-ja-indicator-mode="variant"] > *')];
    const active = segments.findIndex((segment) => segment.classList.contains('is-active'));
    return active >= 0 ? active : 0;
  };

  const isCurrent = (gallery, snapshot) => state.get(gallery) === snapshot;

  const beginSelection = (input) => {
    const card = input.closest('product-card');
    const gallery = galleryOf(card);
    const optionValueId = input.dataset.optionValueId || '';
    if (!(card instanceof HTMLElement) || !gallery || !optionValueId) return;

    const firstImage = imageOf(slidesOf(gallery)[0]);
    const descriptor = descriptorFromInput(input, firstImage);
    if (!descriptor) return;

    const snapshot = { optionValueId, preview: null };

    gallery.dataset.jaActiveOptionValueId = optionValueId;

    snapshot.preview = (async () => {
      if (!(await decodeResource(descriptor, 'high'))) return false;
      if (!isCurrent(gallery, snapshot)) return false;

      const liveImage = imageOf(slidesOf(gallery)[0]);
      if (!liveImage) return false;

      applyImage(liveImage, descriptor);
      return true;
    })();

    state.set(gallery, snapshot);
  };

  const syncSlideIdentity = (liveSlide, sourceSlide) => {
    if (!(liveSlide instanceof HTMLElement) || !(sourceSlide instanceof HTMLElement)) return;

    if (sourceSlide.dataset.jaMediaId) liveSlide.dataset.jaMediaId = sourceSlide.dataset.jaMediaId;
    else delete liveSlide.dataset.jaMediaId;
  };

  const syncIndicatorCount = (gallery, sourceGroup, sourceCount) => {
    const liveIndicator = gallery.querySelector('[data-ja-indicator-mode="variant"]');
    const sourceIndicator = sourceGroup.querySelector('[data-ja-indicator-mode="variant"]');
    if (!(liveIndicator instanceof HTMLElement) || !(sourceIndicator instanceof HTMLElement)) return;

    const liveSegments = [...liveIndicator.children];
    const sourceSegments = [...sourceIndicator.children];
    if (liveSegments.length === sourceCount) return;

    while (liveIndicator.children.length > sourceCount) {
      liveIndicator.lastElementChild?.remove();
    }

    for (let i = liveIndicator.children.length; i < sourceCount; i += 1) {
      const sourceSegment = sourceSegments[i];
      if (sourceSegment) liveIndicator.append(sourceSegment.cloneNode(true));
    }
  };

  const syncSlideCount = (liveTrack, sourceSlides) => {
    let liveSlides = [...liveTrack.querySelectorAll(':scope > [data-ja-slide]')];

    while (liveSlides.length > sourceSlides.length && liveSlides.length > 1) {
      liveSlides.pop()?.remove();
    }

    liveSlides = [...liveTrack.querySelectorAll(':scope > [data-ja-slide]')];
    for (let i = liveSlides.length; i < sourceSlides.length; i += 1) {
      const sourceSlide = sourceSlides[i];
      if (sourceSlide) liveTrack.append(sourceSlide.cloneNode(true));
    }
  };

  const reconcile = async (card, payload, requestOptionValueId) => {
    let gallery = galleryOf(card);
    if (!gallery) return;

    const optionValueId = payload.dataset.jaOptionValueId || requestOptionValueId || '';
    const snapshot = state.get(gallery);
    if (!snapshot || snapshot.optionValueId !== optionValueId) return;

    const fragment = payload.content.cloneNode(true);
    if (!(fragment instanceof DocumentFragment)) return;

    const sourceGroup = fragment.querySelector('[data-ja-group]');
    const sourceTrack = sourceGroup?.querySelector('[data-ja-track]');
    const sourceSlides = sourceTrack ? [...sourceTrack.querySelectorAll('[data-ja-slide]')] : [];
    if (!(sourceGroup instanceof HTMLElement) || !(sourceTrack instanceof HTMLElement) || !sourceSlides.length) return;

    // The first image is already the fast path. If that preload failed, prepare
    // the canonical first image before changing the visible resource.
    const previewReady = await snapshot.preview;

    gallery = galleryOf(card);
    if (!gallery || !isCurrent(gallery, snapshot)) return;

    let liveSlides = slidesOf(gallery);
    const liveTrack = gallery.querySelector('[data-ja-track]');
    if (!(liveTrack instanceof HTMLElement) || !liveSlides.length) return;

    const activeIndex = Math.min(activeIndexOf(gallery), liveSlides.length - 1, sourceSlides.length - 1);
    const activeLiveImage = imageOf(liveSlides[activeIndex]);
    const activeSourceImage = imageOf(sourceSlides[activeIndex]);

    if (!previewReady) {
      const firstLiveImage = imageOf(liveSlides[0]);
      const firstSourceImage = imageOf(sourceSlides[0]);
      if (!firstLiveImage || !firstSourceImage) return;

      const firstDescriptor = descriptorFromImage(firstSourceImage, firstLiveImage);
      if (!firstDescriptor || !(await decodeResource(firstDescriptor, 'high'))) return;
      if (!isCurrent(gallery, snapshot)) return;
      applyImage(firstLiveImage, firstDescriptor);
    }

    // If the customer is currently viewing slide 2+, decode that target before
    // replacing its resource. No scroll/track movement is needed.
    if (activeIndex > 0 && activeLiveImage && activeSourceImage) {
      const activeDescriptor = descriptorFromImage(activeSourceImage, activeLiveImage);
      if (activeDescriptor && !(await decodeResource(activeDescriptor, 'high'))) return;
      if (!isCurrent(gallery, snapshot)) return;
    }

    const liveGroup = gallery.querySelector('[data-ja-group]');
    if (liveGroup instanceof HTMLElement) {
      liveGroup.dataset.jaOptionValueId = optionValueId;
      if (sourceGroup.dataset.jaColor) liveGroup.dataset.jaColor = sourceGroup.dataset.jaColor;
    }

    const sourceTrackLabel = sourceTrack.getAttribute('aria-label');
    if (sourceTrackLabel && sourceTrackLabel !== liveTrack.getAttribute('aria-label')) {
      liveTrack.setAttribute('aria-label', sourceTrackLabel);
    }

    // Structural changes happen only when the mapped groups genuinely contain
    // a different number of images. Slide 1 and the track itself stay in place.
    // different media count. With the normal JA. 2-image groups this is a no-op.
    if (liveSlides.length !== sourceSlides.length) {
      syncSlideCount(liveTrack, sourceSlides);
      syncIndicatorCount(gallery, sourceGroup, sourceSlides.length);
      liveSlides = slidesOf(gallery);

      const segments = [...gallery.querySelectorAll('[data-ja-indicator-mode="variant"] > *')];
      const safeIndex = Math.min(activeIndex, Math.max(segments.length - 1, 0));
      segments.forEach((segment, index) => segment.classList.toggle('is-active', index === safeIndex));
    }

    const count = Math.min(liveSlides.length, sourceSlides.length);
    for (let index = 0; index < count; index += 1) {
      const liveSlide = liveSlides[index];
      const sourceSlide = sourceSlides[index];
      const liveImage = imageOf(liveSlide);
      const sourceImage = imageOf(sourceSlide);
      if (!liveImage || !sourceImage) continue;

      syncSlideIdentity(liveSlide, sourceSlide);

      const descriptor = descriptorFromImage(sourceImage, liveImage);
      if (!descriptor) continue;

      if (index === activeIndex && index > 0) {
        applyImage(liveImage, descriptor);
      } else if (index === 0) {
        // No second visible hand-off: after the instant preview, only the alt
        // text is canonicalized. The already-painted image resource stays put.
        if (!previewReady) applyImage(liveImage, descriptor);
        else liveImage.alt = descriptor.alt;
      } else {
        // Off-screen media can update without blocking the visible interaction.
        applyImage(liveImage, descriptor);
        void decodeResource(descriptor, 'low');
      }
    }

    gallery.dataset.jaActiveOptionValueId = optionValueId;
    gallery.dataset.jaMultipleMedia = sourceSlides.length > 1 ? 'true' : 'false';

    const status = gallery.querySelector('[data-ja-card-media-status]');
    if (status instanceof HTMLElement) {
      status.textContent = sourceSlides.length > 1
        ? `Bild ${activeIndex + 1} von ${sourceSlides.length}.`
        : 'Ein Produktbild verfügbar.';
    }
  };

  document.addEventListener('change', (event) => {
    const input = event.target;
    if (!(input instanceof HTMLInputElement)) return;
    if (!input.matches('swatches-variant-picker-component input[type="radio"][data-ja-preview-src]')) return;
    beginSelection(input);
  }, true);

  const warmFromEvent = (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const input = target.closest('label')?.querySelector('input[data-ja-preview-src]');
    if (!(input instanceof HTMLInputElement)) return;

    const gallery = galleryOf(input.closest('product-card'));
    const firstImage = imageOf(slidesOf(gallery)[0]);
    void decodeResource(descriptorFromInput(input, firstImage), 'high');
  };

  document.addEventListener('pointerover', warmFromEvent, true);
  document.addEventListener('pointerdown', warmFromEvent, true);
  document.addEventListener('focusin', warmFromEvent, true);

  const observer = 'IntersectionObserver' in window
    ? new IntersectionObserver((entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting || !(entry.target instanceof HTMLElement)) continue;

          const card = entry.target;
          const gallery = galleryOf(card);
          const firstImage = imageOf(slidesOf(gallery)[0]);
          const alternate = [...card.querySelectorAll('swatches-variant-picker-component input[data-ja-preview-src]')]
            .find((input) => input instanceof HTMLInputElement && !input.checked);

          if (alternate instanceof HTMLInputElement) {
            void decodeResource(descriptorFromInput(alternate, firstImage), 'low');
          }

          observer.unobserve(card);
        }
      }, { rootMargin: '240px 0px' })
    : null;

  const observeCards = (root) => {
    if (!observer) return;

    if (root instanceof HTMLElement && root.matches('product-card') && !observed.has(root)) {
      observed.add(root);
      observer.observe(root);
    }

    root.querySelectorAll?.('product-card').forEach((card) => {
      if (!(card instanceof HTMLElement) || observed.has(card)) return;
      observed.add(card);
      observer.observe(card);
    });
  };

  observeCards(document);

  if (observer) {
    new MutationObserver((records) => {
      for (const record of records) {
        for (const node of record.addedNodes) {
          if (node instanceof HTMLElement) observeCards(node);
        }
      }
    }).observe(document.documentElement, { childList: true, subtree: true });
  }

  document.addEventListener(StandardEvents.productSelect, (event) => {
    if (!(event.target instanceof Element) || !(event.promise instanceof Promise)) return;

    const card = event.target.closest('product-card');
    if (!(card instanceof HTMLElement)) return;

    const optionValueId = event.detail?.optionValueId || '';

    event.promise
      .then(({ detail }) => {
        const html = detail?.html;
        if (!(html instanceof Document)) return;

        const payload = html.querySelector('template[data-ja-card-media-payload]');
        if (payload instanceof HTMLTemplateElement) void reconcile(card, payload, optionValueId);
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') {
          console.warn('[ja-card-variant-media] Variant media update failed:', error);
        }
      });
  });
}
